import { FC, ReactNode } from 'react';

import '@fontsource-variable/montserrat';
import '@fontsource/roboto';
import '@fontsource-variable/oswald';
import '@fontsource-variable/playfair-display';
import '@fontsource-variable/roboto-slab';
import '@fontsource/pt-mono';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { ruRU as coreRuRU } from '@mui/material/locale';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider
} from '@mui/material/styles';
import { ruRU } from '@mui/x-date-pickers/locales';
import '@mui/x-date-pickers/themeAugmentation';

import { muiComponents } from '@/shared/ui/mui';

const theme = createTheme(
  {
    breakpoints: {
      values: {
        xs: 0,
        xs2: 150,
        xs3: 300,
        xs4: 450,
        sm: 600,
        sm2: 750,
        md: 900,
        md2: 1050,
        lg: 1200,
        lg2: 1350,
        xl: 1536,
        xl2: 1700
      }
    },
    spacing: 4,
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: '#3F51B5',
            dark: '#112D7A',
            light: '#5C6BC0',
            700: '#2E258E',
            800: '#242269'
          },
          secondary: {
            main: '#FFA961',
            dark: '#FF665C',
            light: '#FFC061'
          },
          action: {
            active: '#707070',
            disabled: 'rgba(0, 0, 0, 0.38)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)',
            hover: 'rgba(0, 0, 0, 0.04)'
          },
          text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)'
          },
          error: {
            main: '#D32F2F',
            dark: '#C62828'
          },
          warning: {
            main: '#EF6C00',
            dark: '#663C00'
          },
          info: {
            main: '#0288D1',
            dark: '#01579B'
          },
          success: {
            main: '#2E7D32',
            dark: '#1B5E20'
          },
          gradients: {
            secondary: `radial-gradient(
              circle at right 0% top -10%,
              #FFC061 0%,
              #FFA961 16%,
              #FF665C 50%
            )`
          },
          custom: {
            avatar: 'rgba(189, 189, 189, 1)',
            background: '#F7F9FF',
            border: 'rgba(0, 0, 0, 0.23)',
            tooltip: {
              dark: '#242269E5'
            },
            error: {
              background: '#FDEDED',
              dark: '#5F2120'
            },
            success: {
              background: '#EDF7ED'
            }
          }
        }
      }
    },
    shadows: [
      'none',
      '0px 2px 1px -1px rgba(0, 0, 0, 0.20), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
      '0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 0px 2px 0px rgba(81, 81, 81, 0.14), 0px 1px 6px 0px rgba(81, 81, 81, 0.12)',
      '0px 3px 3px -2px rgba(0, 0, 0, 0.20), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)',
      '0px 2px 5px 0px rgba(81, 81, 81, 0.14), 0px 1px 10px 0px rgba(81, 81, 81, 0.12), 0px 4px 4px 0px rgba(81, 81, 81, 0.10)',
      '0px 3px 5px -1px rgba(0, 0, 0, 0.20), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12)',
      '0px 3px 5px -1px rgba(0, 0, 0, 0.10), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
      '0px 4px 5px -2px rgba(0, 0, 0, 0.20), 0px 7px 10px 1px rgba(0, 0, 0, 0.14), 0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
      '0px 5px 5px -3px rgba(0, 0, 0, 0.20), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
      '0px 5px 6px -3px rgba(0, 0, 0, 0.20), 0px 9px 12px 1px rgba(0, 0, 0, 0.14), 0px 3px 16px 2px rgba(0, 0, 0, 0.12)',
      '0px 6px 6px -3px rgba(0, 0, 0, 0.20), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)',
      '0px 6px 7px -4px rgba(0, 0, 0, 0.20), 0px 11px 15px 1px rgba(0, 0, 0, 0.14), 0px 4px 20px 3px rgba(0, 0, 0, 0.12)',
      '0px 7px 8px -4px rgba(0, 0, 0, 0.20), 0px 12px 17px 2px rgba(0, 0, 0, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
      '0px 7px 8px -4px rgba(0, 0, 0, 0.20), 0px 13px 19px 2px rgba(0, 0, 0, 0.14), 0px 5px 24px 4px rgba(0, 0, 0, 0.12)',
      '0px 7px 9px -4px rgba(0, 0, 0, 0.20), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 5px 26px 4px rgba(0, 0, 0, 0.12)',
      '0px 8px 9px -5px rgba(0, 0, 0, 0.20), 0px 15px 22px 2px rgba(0, 0, 0, 0.14), 0px 6px 28px 5px rgba(0, 0, 0, 0.12)',
      '0px 8px 10px -5px rgba(0, 0, 0, 0.20), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
      '0px 8px 11px -5px rgba(0, 0, 0, 0.20), 0px 17px 26px 2px rgba(0, 0, 0, 0.14), 0px 6px 32px 5px rgba(0, 0, 0, 0.12)',
      '0px 9px 11px -5px rgba(0, 0, 0, 0.20), 0px 18px 28px 2px rgba(0, 0, 0, 0.14), 0px 7px 34px 6px rgba(0, 0, 0, 0.12)',
      '0px 9px 12px -6px rgba(0, 0, 0, 0.20), 0px 19px 29px 2px rgba(0, 0, 0, 0.14), 0px 7px 36px 6px rgba(0, 0, 0, 0.12)',
      '0px 10px 13px -6px rgba(0, 0, 0, 0.20), 0px 20px 31px 3px rgba(0, 0, 0, 0.14), 0px 8px 38px 7px rgba(0, 0, 0, 0.12)',
      '0px 10px 13px -6px rgba(0, 0, 0, 0.20), 0px 21px 33px 3px rgba(0, 0, 0, 0.14), 0px 8px 40px 7px rgba(0, 0, 0, 0.12)',
      '0px 10px 14px -6px rgba(0, 0, 0, 0.20), 0px 22px 35px 3px rgba(0, 0, 0, 0.14), 0px 8px 42px 7px rgba(0, 0, 0, 0.12)',
      '0px 11px 14px -7px rgba(0, 0, 0, 0.20), 0px 23px 36px 3px rgba(0, 0, 0, 0.14), 0px 9px 44px 8px rgba(0, 0, 0, 0.12)',
      '0px 11px 15px -7px rgba(0, 0, 0, 0.20), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)'
    ],
    typography: {
      fontFamily: 'Montserrat Variable, Roboto, Helvetica, Arial, sans-serif',
      h1: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif'
      },
      h2: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif'
      },
      h3: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif'
      },
      h4: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif'
      },
      h5: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontWeight: 600
      },
      h6: {
        fontSize: '1.125rem',
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontWeight: 600
      },
      h7: {
        fontSize: '1rem',
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 500,
        textTransform: 'none',
        textDecorationLine: 'none'
      },
      h8: {
        fontSize: '1rem',
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontWeight: 500,
        color: 'palette.text.primary'
      },
      subtitle1: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        // MUI по умолчанию даёт subtitle1 интерлиньяж 1.75 — это шаг основного
        // текста, а не заголовка. На заголовке карточки списка (16px) он давал
        // 28px строк: две строки разъезжались и читались как два отдельных
        // элемента, а карточка уведомления росла с 70 до 98px. 1.4 — тот же
        // приём, что уже применён к subtitle2 (1.21): интерлиньяж заголовочных
        // ролей задаётся в теме, а не на экране.
        lineHeight: 1.4
      },
      subtitle2: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontWeight: 400,
        lineHeight: 1.21
      },
      overline: {
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif'
      },
      caption: {
        fontSize: '0.625rem',
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif'
      },
      'body/montserrat/12': {
        fontSize: '0.75rem',
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 500,
        textTransform: 'none',
        textDecorationLine: 'none'
      },
      'chip/label': {
        fontSize: '13px',
        fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 500,
        textTransform: 'none',
        textDecorationLine: 'none'
      },
      'body/roboto/16': {
        fontSize: '1rem',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        textTransform: 'none',
        textDecorationLine: 'none'
      },
      'body/roboto/14': {
        fontSize: '0.875rem',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        textTransform: 'none',
        textDecorationLine: 'none'
      },
      'body/roboto/12': {
        fontSize: '0.75rem',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        textTransform: 'none',
        textDecorationLine: 'none'
      }
    },
    shape: {
      borderL: '16px',
      borderM: '8px',
      borderS: '4px'
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: '',
            margin: 0,
            // dvh, а не vh: на мобилке/планшете vh = «большой» вьюпорт (с
            // адресной строкой), из-за чего низ приложения (фикс-бары) уезжал
            // под браузерную обвязку. dvh следует за реальной видимой высотой.
            // На десктопе dvh == vh.
            height: '100dvh',
            backgroundImage: `
            linear-gradient(
              to right top,
              #201d62, #1c1c59, #191a50, #161948, #14173f, #141840, #141940, #141a41, #141e4c, #142257, #122762, #0e2b6e
            )
          `,
            overflow: 'hidden'
          },
          // Стили для скроллбара
          // Chrome, Safari, Edge
          '*::-webkit-scrollbar': {
            width: '6px',
            backgroundColor: 'transparent'
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '24px'
          },
          // IE 11
          '*::-ms-scrollbar': {
            width: '6px'
          },
          '*::-ms-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '24px'
          }
        }
      },
      ...muiComponents
    }
  },
  coreRuRU,
  ruRU
);

const globalStyles = (
  <GlobalStyles
    styles={`
      @property --outlined-secondary-gradient-percent-1 {
        syntax: "<percentage>";
        initial-value: 0%;
        inherits: false;
      }
      @property --outlined-secondary-gradient-percent-2 {
        syntax: "<percentage>";
        initial-value: 16%;
        inherits: false;
      }
      @property --outlined-secondary-gradient-percent-3 {
        syntax: "<percentage>";
        initial-value: 50%;
        inherits: false;
      }
    `}
  />
);

/**
 * Провайдер темы
 * @param root0 - функциональный компонент
 * @param root0.children - дочерние элементы
 */
export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <MUIThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      {children}
    </MUIThemeProvider>
  );
};
